'use client';
import { useEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';
import { usePathname, useSearchParams } from 'next/navigation';
import { useClickOutside } from '@overlapdev/hooks';
import { updateSearchParams } from '@overlapdev/functions';
import { Button } from '@overlapdev/elements';
import { TaxonomyType } from '@overlapdev/types';
import { Close } from 'flowbite-react-icons/outline';
import { ArrowDownSVG, ArrowUpSVG, FilterSVG } from 'components/svg';

const EventsFilters = ({ taxonomy }: { taxonomy?: TaxonomyType[] }) => {
	//__Variables
	const taxonomy_slugs = taxonomy?.map((e) => e.slug);

	//__Refs
	const wrapperRef = useRef(null);

	//__Hooks
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const clickOutside = useClickOutside(wrapperRef);

	//__State
	const [filters, setFilters] = useState<any>({});
	const [open, setOpen] = useState('');
	const [openFilters, setOpenFilters] = useState(false);

	//__Use Effect
	//Setup intitial filters state based on taxonomy
	useEffect(() => {
		taxonomy?.map((item) => {
			if (filters[item.slug] === undefined) {
				setFilters((filters: any) => ({
					...filters,
					[item.slug]: []
				}));
			}
		});
	}, [taxonomy]);

	//Update filters state if searchParams present
	useEffect(() => {
		searchParams.forEach((value, key) => {
			if (taxonomy_slugs?.includes(key) && filters[key] !== value.split(',').map(Number)) {
				setFilters((filters: any) => ({
					...filters,
					[key]: value.split(',').map(Number)
				}));
			}
		});
	}, [searchParams]);

	useEffect(() => {
		if (clickOutside) {
			setOpen('');
			setOpenFilters(false);
		}
	}, [clickOutside]);

	useEffect(() => {
		if (openFilters) {
			document.documentElement.style.overflow = 'hidden';
			document.body.classList.add('filters_open');
		} else {
			document.documentElement.style.overflow = 'unset';
			document.body.classList.remove('filters_open');
		}
	}, [openFilters]);

	//__Functions
	const toggle = (tax_slug: string, term_id: number) => {
		const filters_copy = filters;
		if (filters_copy[tax_slug]) {
			if (filters_copy[tax_slug].includes(term_id)) {
				filters_copy[tax_slug] = [];
			} else {
				filters_copy[tax_slug] = [term_id];
			}
			setFilters((filters: any) => ({
				...filters,
				[tax_slug]: filters_copy[tax_slug]
			}));
			window.history.pushState(null, '', `${pathname}?${updateSearchParams(searchParams.toString(), tax_slug, filters_copy[tax_slug].toString(), 'p')}`);
		}
		setOpen('');
	};

	const clear = (tax_slug: string) => {
		const filters_copy = filters;
		if (filters_copy[tax_slug]) {
			filters_copy[tax_slug] = [];
			window.history.pushState(null, '', `${pathname}?${updateSearchParams(searchParams.toString(), tax_slug, null, 'p')}`);
		}
	};

	if (!taxonomy) return null;
	return (
		<div ref={wrapperRef} className='font-semibold'>
			<div className='border border-black/10 cursor-pointer flex items-center justify-center h-11 relative rounded-full w-11 lg:hidden' onClick={() => setOpenFilters(!openFilters)} role='button'>
				<FilterSVG className='text-black' width={18} />
				{Object.keys(filters)
					.map((e) => filters[e])
					.find((e) => e.length) ? (
					<div className='absolute bg-primary h-2 -right-0.5 rounded-full -top-0.5 w-2' />
				) : null}
			</div>
			<div className='flex flex-wrap gap-6 max-lg:hidden'>
				{Object.keys(filters).length
					? taxonomy?.map((tax) =>
							tax?.terms.find((e) => e.count > 0) ? (
								<div key={`filter_${tax.slug}`} className='flex gap-2 items-center relative'>
									<div className='text-grey'>{parse(tax.name)}</div>
									<div className='relative'>
										<div
											className={`border border-grey cursor-pointer flex gap-4 items-center justify-between leading-none py-[13px] pl-5 pr-3 rounded-full text-black`}
											onClick={() => {
												if (open === tax.slug) {
													setOpen('');
												} else {
													setOpen(tax.slug);
												}
											}}
										>
											<div className='overflow-hidden pr-3 whitespace-nowrap w-[82%]'>{filters[tax.slug]?.length ? <span>{parse(`${taxonomy.find((e) => e.slug === tax.slug)?.terms.find((e) => e.term_id === filters[tax.slug][0])?.name}${tax.slug === 'beds' || tax.slug === 'baths' ? '+' : ''}` || '')}</span> : <span>Any</span>}</div>
											<div className='opacity-60'>{open === tax.slug ? <ArrowUpSVG width={12} /> : <ArrowDownSVG width={12} />}</div>
											{filters[tax.slug].length ? <div className='absolute bg-primary h-2 -right-1 rounded-full -top-1 w-2' /> : null}
										</div>
										<div className={`absolute bg-white flex flex-col gap-2 left-0 min-w-full overflow-hidden rounded-3xl shadow-[0px_20px_30px_0px_rgba(0,0,0,0.15)] top-full transition-ease z-10 ${open === tax.slug ? 'max-h-[1000px] py-5' : 'max-h-0'}`}>
											{tax?.terms.map((term) =>
												term.term_id !== 25 && term.term_id !== 26 ? (
													<div key={`term_${term.term_id}`} className=''>
														<input className='hidden' type='checkbox' id={`term_${term.term_id}`} name={`filter_${tax.slug}`} value={term.term_id} checked={filters[tax.slug]?.includes(term.term_id)} onChange={() => toggle(tax.slug, term.term_id)} />
														<label className={`cursor-pointer block font-semibold px-5 text-base text-black transition-ease whitespace-nowrap hover:text-primary ${filters[tax.slug]?.includes(term.term_id) ? ' text-primary' : ''}`} htmlFor={`term_${term.term_id}`}>
															{parse(`${term.name}${tax.slug === 'beds' || tax.slug === 'baths' ? '+' : ''}`)}
														</label>
													</div>
												) : null
											)}
										</div>
										{filters[tax.slug]?.length ? (
											<span className='absolute cursor-pointer m-auto right-7 top-3' role='button' onClick={() => clear(tax.slug)}>
												<Close size={20} className='text-black/80' />
												<span className='hidden'>Clear</span>
											</span>
										) : null}
									</div>
								</div>
							) : null
					  )
					: null}
			</div>
			<div className={`bg-black bottom-0 fixed left-0 overflow-auto px-6 text-white transition-ease w-full z-90 lg:hidden ${openFilters ? 'h-[calc(100svh-94px)] py-6' : 'h-0'}`}>
				<div className='flex flex-col gap-6'>
					{Object.keys(filters).length
						? taxonomy?.map((tax) =>
								tax?.terms.find((e) => e.count > 0) ? (
									<div key={`filter_${tax.slug}`} className='relative'>
										{filters[tax.slug]?.length ? (
											<div className='absolute bg-white flex gap-1 h-5 items-center px-2 right-0 rounded-full text-black top-0' role='button' onClick={() => clear(tax.slug)}>
												<span className='text-xs'>Clear Filter</span>
												<Close size={16} className='text-black' />
											</div>
										) : null}
										<div className='text-white w-1/5'>{parse(tax.name)}</div>
										<div className='relative'>
											<div
												className={`bg-white border border-grey cursor-pointer flex gap-4 items-center justify-between leading-none py-[13px] pl-5 pr-3 rounded-full text-black`}
												onClick={() => {
													if (open === tax.slug) {
														setOpen('');
													} else {
														setOpen(tax.slug);
													}
												}}
											>
												<div className='overflow-hidden pr-3 whitespace-nowrap w-[82%]'>{filters[tax.slug]?.length ? <span>{parse(`${taxonomy.find((e) => e.slug === tax.slug)?.terms.find((e) => e.term_id === filters[tax.slug][0])?.name}${tax.slug === 'beds' || tax.slug === 'baths' ? '+' : ''}` || '')}</span> : <span>Any</span>}</div>
												<div className='opacity-60'>{open === tax.slug ? <ArrowUpSVG width={12} /> : <ArrowDownSVG width={12} />}</div>
												{filters[tax.slug].length ? <div className='absolute bg-primary h-2 -right-1 rounded-full -top-1 w-2' /> : null}
											</div>
											<div className={`absolute bg-white flex flex-col gap-2 left-0 min-w-full overflow-hidden rounded-3xl shadow-[0px_20px_30px_0px_rgba(0,0,0,0.15)] top-full transition-ease z-10 ${open === tax.slug ? 'max-h-[1000px] py-5' : 'max-h-0'}`}>
												{tax?.terms.map((term) =>
													term.term_id !== 25 && term.term_id !== 26 ? (
														<div key={`term_${term.term_id}`} className=''>
															<input className='hidden' type='checkbox' id={`term_${term.term_id}`} name={`filter_${tax.slug}`} value={term.term_id} checked={filters[tax.slug]?.includes(term.term_id)} onChange={() => toggle(tax.slug, term.term_id)} />
															<label className={`cursor-pointer block font-semibold px-5 text-base text-black transition-ease whitespace-nowrap hover:text-primary ${filters[tax.slug]?.includes(term.term_id) ? ' text-primary' : ''}`} htmlFor={`term_${term.term_id}`}>
																{parse(`${term.name}${tax.slug === 'beds' || tax.slug === 'baths' ? '+' : ''}`)}
															</label>
														</div>
													) : null
												)}
											</div>
										</div>
									</div>
								) : null
						  )
						: null}
				</div>
				<div className='mt-8'>
					<Button className='btn cursor-pointer' link={{ url: '', title: 'Apply Filters' }} onClick={() => setOpenFilters(false)} />
				</div>
				<span className='absolute m-auto right-2 top-2' role='button' onClick={() => setOpenFilters(false)}>
					<Close size={28} className='text-white' />
					<span className='hidden'>Close</span>
				</span>
			</div>
		</div>
	);
};

export default EventsFilters;
